import React, { useState } from 'react';
import Modal from '../../../../../Common/Components/Modal';
import AccountForm from './AccountForm';
import EditAccountModalCustomTitle from './EditAccountModalCustomTitle';
import { useAppContext } from '../../../../../utils/AppContext';
import { postRequest, putRequest } from '../../../../../utils/fetch';
import formValidation from '../../../../../utils/form/formValidation';
import { AccountModalProps } from '../../../../../types/AccountModal';
import { CustomerObject } from '../../../../../types/CustomerObject';
import { ModalType } from '../../../../../types/modal';

function AccountModal(props: AccountModalProps): JSX.Element {
  const { type, setModalOpen, currentPartner, partners, customer, updateCustomers } = props;
  const customerItem = customer as CustomerObject;
  const [accountData, setAccountData] = useState({
    name: customerItem.name,
    type: customerItem.type || '',
    street1: customerItem.street1,
    street2: customerItem.street2,
    city: customerItem.city,
    state: customerItem.state,
    postal_code: customerItem.postal_code,
    country: customerItem.country,
    contact_email: customerItem.contact_email,
    contact_phone: customerItem.contact_phone,
    contact_name: customerItem.contact_name,
    latitude: customerItem.latitude,
    longitude: customerItem.longitude,
    vzids: Array.isArray(customerItem.vzids) ? customerItem.vzids : [],
    notes: customerItem.notes,
    ecpd: Array.isArray(customerItem.ecpd) ? customerItem.ecpd : [],

  });

  const [accountValidation, setAccountValidation] = useState({
    hasError: false,
    errorMessage: '',
    hasNameError: false,
    nameErrorMessage: '',
    hasNumError: false,
    numErrorMessage: '',
    hasLatError: false,
    LatErrorMessage: '',
    hasLonError: false,
    LonErrorMessage: '',
    hasNotesError: false,
    NotesErrorMessage: '',
  });
  const [submitDisabled, setSubmitDisabled] = useState<boolean>(false);
  let modalProps: ModalType = {
    secondaryButtonLabel: 'Cancel',
    setModalOpen: () => setModalOpen(0),
  };
  if (type === 'create') {
    modalProps = {
      ...modalProps,
      title: 'Create account',
      primaryButtonLabel: 'Create',
      primaryButtonDisabled: submitDisabled,
    };
  } else {
    modalProps = {
      ...modalProps,
      title: 'Edit Account',
      primaryButtonLabel: 'Save',
      customTitle: <EditAccountModalCustomTitle item={customerItem} title="Edit account" />,
    };
  }

  const [selectedPartner, setSelectedPartner] = useState({ id: currentPartner?.orgid || '', name: currentPartner?.name || '' });
  const { addNotification } = useAppContext();

  const setName = (newName?: string) => {
    const name = newName !== undefined ? newName : accountData.name || '';
    const validation = formValidation(
      name,
      { required: true, min: 2, max: 256, noSpecCharacters: true },
    );

    setAccountData((oldValues) => ({
      ...oldValues,
      name,
    }));

    setAccountValidation((oldValues) => ({
      ...oldValues,
      hasNameError: validation.hasError,
      nameErrorMessage: validation.errorMsg,
    }));
  };

  const setVzids = (vzids: string[]) => {
    /*
    const validation = formValidation(
      vzids,
      { required: true, checkVzids: true },
    );

    setAccountValidation((oldValues) => ({
      ...oldValues,
      hasNumError: validation.hasError,
      numErrorMessage: validation.errorMsg,
    }));

    */

    setAccountData((oldValues) => ({
      ...oldValues,
      vzids,
    }));
  };

  const setGps = (latitude: string, longitude: string) => {
    const validationLatitude = formValidation(
      latitude,
      { required: true, checkNumberFormat: true, floatNumberInRange: { from: -90.0, to: 90.0 } },
    );
    const validationLongitude = formValidation(
      longitude,
      { required: true, checkNumberFormat: true, floatNumberInRange: { from: -180.0, to: 180.0 } },
    );

    setAccountValidation((oldValues) => ({
      ...oldValues,
      hasLatError: validationLatitude.hasError,
      LatErrorMessage: validationLatitude.errorMsg,
      hasLonError: validationLongitude.hasError,
      LonErrorMessage: validationLongitude.errorMsg,
    }));

    setAccountData((oldValues) => ({
      ...oldValues,
      latitude,
      longitude,
    }));
  };

  return (
    <Modal
      width="930"
      className="padding-30"
      {...modalProps}
      primaryButtonAction={async () => {
        if (accountData.name === '') {
          setName(accountData.name);
          return;
        }
        if ((accountData.notes !== '') && (accountData.notes !== null) && (accountData.notes !== undefined)) {
          const accountNotesRegex = /^[a-zA-Z0-9]{0,500}$/;
          const invalidNotesError = accountData.notes.match(accountNotesRegex) === null;
          setAccountValidation((oldValues) => ({
            ...oldValues,
            hasNotesError: invalidNotesError,
            NotesErrorMessage: invalidNotesError ? 'Notes may have upto a maximum of 500 alphanumeric characters.' : '',
          }));
          if (invalidNotesError) {
            return;
          }
        }

        setVzids(accountData.vzids);
        setGps(accountData.latitude, accountData.longitude);
        try {
          const latitude = parseFloat(accountData.latitude);
          const longitude = parseFloat(accountData.longitude);

          const postBody = {
            name: accountData.name,
            type: accountData.type,
            street1: accountData.street1,
            street2: accountData.street2,
            city: accountData.city,
            state: accountData.state,
            postal_code: accountData.postal_code,
            country: accountData.country,
            contact_email: accountData.contact_email,
            contact_phone: accountData.contact_phone,
            contact_name: accountData.contact_name,
            latitude,
            longitude,
            vzids: accountData.vzids.map((id: string) => id.trim()),
            ecpd: accountData.ecpd?.map((id: string) => id.trim()),
            notes: accountData.notes,
          };

          let result;

          if (type === 'create') {
            setSubmitDisabled(true);
            result = await postRequest(`/organizations/${selectedPartner?.id}/customers`, postBody);
          } else {
            result = await putRequest(`/organizations/${customerItem.orgid}`, postBody);
          }

          if (!result.error) {
            addNotification({ type: 'success', message: `Your ${modalProps.title} operation is completed.` });
            updateCustomers();
            setModalOpen(0);
          } else {
            setSubmitDisabled(false);
            addNotification({ type: 'error', message: `Your ${modalProps.title} operation has failed: ${result.error}` });
          }
        } catch (error) {
          setSubmitDisabled(false);
          addNotification({ type: 'error', message: `Your ${modalProps.title} operation has failed.` });
        }
      }}
    >
      <AccountForm
        data={accountData}
        setAccount={setAccountData}
        partners={partners}
        selectedPartner={selectedPartner}
        setSelectedPartner={setSelectedPartner}
        currentPartner={currentPartner}
        validation={accountValidation}
      />
    </Modal>
  );
}

export default AccountModal;
